import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions';

export const RequestsNewService = {
  async createRequest(userId: string, data: any) {
    await permissionEngine.assert(userId, 'inventory.requests.create', { 
      companyId: data.companyId,
      projectId: data.projectId 
    });
    
    const { data: request, error: reqError } = await supabase
      .from('requests')
      .insert({
        company_id: data.companyId,
        project_id: data.projectId,
        zone_id: data.zoneId || null,
        request_code: `REQ-${Math.floor(Date.now() / 1000)}`, // Tạm thời gen code random, sau này dùng sequence
        title: data.title,
        request_type: data.requestType || 'thi_cong',
        priority_code: data.customData?.priority || 'normal',
        status_code: 'draft',
        description: data.description,
        reason: data.customData?.reason,
        impact_scope: data.customData?.impactScope,
        related_asset_id: data.customData?.relatedAssetId || null,
        proposal_group: data.customData?.group,
        proposal_category: data.customData?.category,
        proposal_purpose: data.customData?.purpose,
        deadline_date: data.customData?.deadlineDate || null,
        expected_date: data.customData?.expectedDate || null,
        requested_by: userId,
        related_user_id: data.customData?.relatedUserId || null,
      })
      .select()
      .single();

    if (reqError) throw reqError;

    // Insert request items if any
    if (data.items && data.items.length > 0) {
      const { error: itemsError } = await supabase
        .from('request_items')
        .insert(data.items.map((item: any) => ({
          request_id: request.id,
          item_name: item.name,
          quantity: item.qty,
          unit: item.unit,
          notes: item.reason || item.notes
        })));
      
      if (itemsError) console.error('Error inserting items:', itemsError);
    }

    return request;
  },

  async getProjects(companyId: string) {
    const { data, error } = await supabase
      .from('projects')
      .select('id, code, name')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .is('deleted_at', null)
      .order('name');
    if (error) throw error;
    return data;
  },

  async getZones(projectId: string) {
    if (!projectId) return [];
    const { data, error } = await supabase
      .from('project_zones')
      .select('id, name:zone_name, code:zone_code')
      .eq('project_id', projectId);
    if (error) throw error;
    return data;
  },

  async getProposalGroups(companyId: string) {
    // Get parent category Nhom-De-Xuat
    const { data: parent } = await supabase
      .from('categories')
      .select('id')
      .eq('company_id', companyId)
      .eq('code', 'Nhom-De-Xuat')
      .maybeSingle();

    if (!parent) return [];

    const { data, error } = await supabase
      .from('categories')
      .select('id, name, code')
      .eq('company_id', companyId)
      .eq('parent_id', parent.id)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return data;
  },

  async getProposalCategories(companyId: string, parentId: string) {
    if (!parentId) return [];
    
    const { data, error } = await supabase
      .from('categories')
      .select('id, name, code')
      .eq('company_id', companyId)
      .eq('parent_id', parentId)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return data;
  },

  async getProposalPurposes(companyId: string) {
    const { data: parent } = await supabase
      .from('categories')
      .select('id')
      .eq('company_id', companyId)
      .eq('code', 'Muc-Dich')
      .maybeSingle();

    if (!parent) return [];

    const { data, error } = await supabase
      .from('categories')
      .select('id, name, code')
      .eq('company_id', companyId)
      .eq('parent_id', parent.id)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return data;
  },

  async getApprovers(companyId: string) {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, full_name, job_title')
      .eq('company_id', companyId)
      .neq('role_code', 'member')
      .eq('is_active', true);

    if (error) throw error;
    return data;
  }
};
